import { DocumentStatus } from '@synergy/models';
import { createSelector } from 'reselect';

import { AppState } from '../../../../../store/models/app-state';
import { InvoiceProductItem } from '../../../models/invoice-product-item';
import { PositionsFormValues } from '../../models/positions-form-values';
import { StepData } from '../../../../../common/components/new-stepper/step-data';

const invoiceDetailPositionsState = (state: AppState) => state.invoiceDetail.positions;
const invoiceDetailOverviewState = (state: AppState) => state.invoiceDetail.overview;
const invoiceDetailPositionsStepDataState = createSelector(invoiceDetailPositionsState, state => state.stepData);

/**
 * The defaults the step fills in for values the server left empty.
 *
 * Shared with the unsaved-changes baseline (#2159): compared against raw server
 * values, every one of these would read as something the user typed.
 */
const withDefaults = (values: PositionsFormValues): PositionsFormValues => ({
    ...values,
    netShippingCosts: values.netShippingCosts ?? '',
    netSurcharge: values.netSurcharge ?? '',
    isTaxFree: values.isTaxFree ?? false,
    allItems: values.allItems ?? [],
});

export const invoiceDetailPositionsDataSelector = createSelector(
    invoiceDetailPositionsStepDataState,
    (stepData): StepData<PositionsFormValues> => ({ ...stepData, values: withDefaults(stepData.values) })
);

/**
 * Whether a sent invoice's lines may still change their account, and when it
 * went to BMD otherwise (#2326 R-239, GF 28.10.2026).
 */
export const counterAccountChangeSelector = createSelector(
    invoiceDetailPositionsState,
    invoiceDetailOverviewState,
    (positions, overview) => ({
        changeable: DocumentStatus.Completed === overview.stepData.values.status && !positions.exportedToAccountingAt,
        exportedAt: positions.exportedToAccountingAt ?? null,
    })
);

/** What the step compares against to decide whether anything is unsaved. */
export const invoiceDetailPositionsBaselineSelector = createSelector(invoiceDetailPositionsState, state =>
    state.baseline ? withDefaults(state.baseline) : undefined
);

export const invoiceDetailPositionProductItemSelector = createSelector(
    invoiceDetailPositionsStepDataState,
    (state): InvoiceProductItem[] => state.values.allItems.filter(item => 'tax' in item) as InvoiceProductItem[]
);

export const invoiceDetailRecipientTaxRateSelector = createSelector(
    invoiceDetailPositionsState,
    state => state.recipientTaxRateId
);

/**
 * Whether the invoice is a credit note. The flag the toggle turns at once, not
 * the head's values, which follow only when the head is handed on: a new
 * credit note took the first article at its positive price and offered
 * "Rechnung abschließen" (R-203, R-204).
 */
export const isCreditSelector = createSelector(invoiceDetailOverviewState, state => state.isCredit);
